// src/pages/inventory/EditPurchase.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    TextField,
    Grid,
    MenuItem,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    CircularProgress,
    Divider,
    Chip,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    ShoppingCart as ShoppingCartIcon,
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    QrCodeScanner as ScanIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { format } from 'date-fns';

const EditPurchase = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [suppliers, setSuppliers] = useState([]);
    const [products, setProducts] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [originalPurchase, setOriginalPurchase] = useState(null);
    const [purchaseData, setPurchaseData] = useState({
        supplier_id: '',
        purchase_date: '',
        reference_number: '',
        status: 'pending',
        payment_status: 'pending',
        account_id: '',
        notes: '',
        items: []
    });
    const [currentItem, setCurrentItem] = useState({
        item_id: '',
        quantity: 1,
        unit_price: 0,
        notes: ''
    });
    const [totalAmount, setTotalAmount] = useState(0);

    // Series capturing states
    const [seriesDialogOpen, setSeriesDialogOpen] = useState(false);
    const [tempSeries, setTempSeries] = useState([]); // [{ serial_number, mac_address, condition }]
    const [seriesError, setSeriesError] = useState(null);
    const [activeItemIndex, setActiveItemIndex] = useState(null);

    useEffect(() => {
        fetchInitialData();
    }, [id]);

    useEffect(() => {
        // Calcular el monto total cuando cambian los items
        const total = purchaseData.items.reduce((sum, item) => {
            return sum + (item.quantity * item.unit_price);
        }, 0);
        setTotalAmount(total);
    }, [purchaseData.items]);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            if (!token) {
                navigate('/login');
                return;
            }

            // Fetch suppliers
            const suppliersResponse = await fetch('/api/inventory/suppliers', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!suppliersResponse.ok) {
                if (suppliersResponse.status === 401) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    navigate('/login');
                    return;
                }
                throw new Error('Error al obtener proveedores');
            }

            const suppliersData = await suppliersResponse.json();
            setSuppliers(suppliersData);

            // Fetch products
            const productsResponse = await fetch('/api/inventory/items', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!productsResponse.ok) {
                if (productsResponse.status === 401) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    navigate('/login');
                    return;
                }
                throw new Error('Error al obtener productos');
            }

            const productsData = await productsResponse.json();
            setProducts(productsData);

            // Fetch financial accounts for payment allocation
            const accountsResponse = await fetch('/api/accounting/accounts', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (accountsResponse.ok) {
                const accountsData = await accountsResponse.json();
                setAccounts(accountsData.filter(a => a.status === 'active'));
            }

            // Fetch purchase details
            const purchaseResponse = await fetch(`/api/inventory/purchases/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!purchaseResponse.ok) {
                if (purchaseResponse.status === 401) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    navigate('/login');
                    return;
                }

                if (purchaseResponse.status === 404) {
                    setError('Orden de compra no encontrada');
                    // Redireccionar después de 3 segundos
                    setTimeout(() => {
                        navigate('/inventory/purchases');
                    }, 3000);
                    return;
                }

                throw new Error('Error al obtener detalles de la orden de compra');
            }

            const purchaseData = await purchaseResponse.json();
            setOriginalPurchase(purchaseData);

            // Formatear los datos para el formulario
            const formattedDate = purchaseData.purchase_date
                ? new Date(purchaseData.purchase_date).toISOString().split('T')[0]
                : new Date().toISOString().split('T')[0];

            // Preparar los items con nombres de productos
            const itemsWithNames = purchaseData.items?.map(item => {
                const product = productsData.find(p => p.id === item.item_id);
                return {
                    ...item,
                    item_name: product ? product.name : 'Producto desconocido',
                    subtotal: item.quantity * item.unit_price
                };
            }) || [];

            setPurchaseData({
                supplier_id: purchaseData.supplier_id || '',
                purchase_date: formattedDate,
                reference_number: purchaseData.reference_number || '',
                status: purchaseData.status || 'pending',
                payment_status: purchaseData.payment_status || 'pending',
                account_id: purchaseData.account_id || '',
                notes: purchaseData.notes || '',
                items: itemsWithNames
            });

        } catch (error) {
            console.error('Error fetching purchase data:', error);
            setError(error.message || 'Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setPurchaseData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleItemInputChange = (e) => {
        const { name, value } = e.target;

        // Si el campo es item_id, buscar el precio de compra del producto
        if (name === 'item_id' && value) {
            const selectedProduct = products.find(product => product.id === value);
            if (selectedProduct) {
                setCurrentItem(prev => ({
                    ...prev,
                    [name]: value,
                    unit_price: parseFloat(selectedProduct.purchase_price)
                }));
                return;
            }
        }

        setCurrentItem(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Series capture
    const handleOpenSeriesDialog = (index) => {
        const item = purchaseData.items[index];
        setActiveItemIndex(index);
        setTempSeries(item.units && item.units.length === item.quantity 
            ? JSON.parse(JSON.stringify(item.units))
            : Array.from({ length: item.quantity }, () => ({ serial_number: '', mac_address: '', condition: 'new' }))
        );
        setSeriesError(null);
        setSeriesDialogOpen(true);
    };

    const handleTempSeriesChange = (index, field, value) => {
        setTempSeries(prev => prev.map((item, idx) => {
            if (idx === index) {
                return { ...item, [field]: value };
            }
            return item;
        }));
    };

    const handleSaveSeries = () => {
        setSeriesError(null);
        const serials = new Set();
        const macs = new Set();

        for (let i = 0; i < tempSeries.length; i++) {
            const u = tempSeries[i];
            const cleanSN = u.serial_number.toUpperCase().trim();
            const cleanMAC = u.mac_address.toUpperCase().trim();

            if (!cleanSN || !cleanMAC) {
                setSeriesError(`Por favor complete todos los campos de S/N y MAC (Fila #${i + 1}).`);
                return;
            }

            if (serials.has(cleanSN)) {
                setSeriesError(`Número de serie duplicado localmente: ${cleanSN}`);
                return;
            }
            if (macs.has(cleanMAC)) {
                setSeriesError(`Dirección MAC duplicada localmente: ${cleanMAC}`);
                return;
            }

            serials.add(cleanSN);
            macs.add(cleanMAC);
        }

        setPurchaseData(prev => {
            const updatedItems = [...prev.items];
            updatedItems[activeItemIndex] = {
                ...updatedItems[activeItemIndex],
                units: tempSeries.map(u => ({
                    serial_number: u.serial_number.toUpperCase().trim(),
                    mac_address: u.mac_address.toUpperCase().trim(),
                    condition: u.condition || 'new'
                }))
            };
            return {
                ...prev,
                items: updatedItems
            };
        });

        setSeriesDialogOpen(false);
    };

    const addItemToPurchase = () => {
        if (!currentItem.item_id || currentItem.quantity <= 0 || currentItem.unit_price <= 0) {
            setError('Por favor complete todos los campos requeridos del producto');
            return;
        }

        // Buscar el producto seleccionado para obtener el nombre
        const selectedProduct = products.find(product => product.id === currentItem.item_id);

        // Agregar item con información extendida
        const newItem = {
            ...currentItem,
            item_name: selectedProduct ? selectedProduct.name : 'Producto desconocido',
            subtotal: currentItem.quantity * currentItem.unit_price,
            units: []
        };

        setPurchaseData(prev => ({
            ...prev,
            items: [...prev.items, newItem]
        }));

        // Resetear formulario de item
        setCurrentItem({
            item_id: '',
            quantity: 1,
            unit_price: 0,
            notes: ''
        });
    };

    const removeItem = (index) => {
        setPurchaseData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async () => {
        // Validar datos
        if (!purchaseData.supplier_id) {
            setError('Por favor seleccione un proveedor');
            return;
        }

        if (purchaseData.payment_status === 'paid' && !purchaseData.account_id) {
            setError('Por favor seleccione una Cuenta de Pago para registrar el egreso contable');
            return;
        }

        if (purchaseData.items.length === 0) {
            setError('Debe agregar al menos un producto a la orden de compra');
            return;
        }

        // Validar que se hayan cargado las series si la orden se marca como recibida
        if (purchaseData.status === 'received') {
            for (const item of purchaseData.items) {
                const product = products.find(p => p.id === item.item_id);
                if (product && product.equipment_type) {
                    const isConfigured = item.units && 
                                         item.units.length === item.quantity && 
                                         item.units.every(u => u.serial_number && u.mac_address);
                    if (!isConfigured) {
                        setError(`Debe configurar todas las series y MACs para el equipo: ${item.item_name}`);
                        return;
                    }
                }
            }
        }

        try {
            setSubmitting(true);
            setError(null);

            const token = localStorage.getItem('token');

            if (!token) {
                navigate('/login');
                return;
            }

            // Preparar datos para enviar
            const submitData = {
                ...purchaseData,
                total_amount: totalAmount
            };

            const response = await fetch(`/api/inventory/purchases/${id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(submitData)
            });

            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    navigate('/login');
                    return;
                }

                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al actualizar la orden de compra');
            }

            const result = await response.json();
            setSuccess('Orden de compra actualizada exitosamente');

            // Redireccionar después de un tiempo
            setTimeout(() => {
                navigate('/inventory/purchases');
            }, 2000);

        } catch (error) {
            console.error('Error updating purchase:', error);
            setError(error.message || 'Error al actualizar la orden de compra');
        } finally {
            setSubmitting(false);
        }
    };

    // Función para obtener el chip de estado
    const getStatusChip = (status) => {
        switch (status) {
            case 'pending':
                return <Chip label="Pendiente" color="warning" size="small" />;
            case 'partial':
                return <Chip label="Parcial" color="info" size="small" />;
            case 'received':
                return <Chip label="Recibido" color="success" size="small" />;
            case 'cancelled':
                return <Chip label="Cancelado" color="error" size="small" />;
            default:
                return <Chip label={status} size="small" />;
        }
    };

    const getPaymentStatusChip = (status) => {
        switch (status) {
            case 'pending':
                return <Chip label="Pendiente" color="warning" size="small" />;
            case 'partial':
                return <Chip label="Parcial" color="info" size="small" />;
            case 'paid':
                return <Chip label="Pagado" color="success" size="small" />;
            case 'cancelled':
                return <Chip label="Cancelado" color="error" size="small" />;
            default:
                return <Chip label={status} size="small" />;
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const isEditable = originalPurchase &&
        originalPurchase.status !== 'received' &&
        originalPurchase.status !== 'cancelled';

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton
                    sx={{ mr: 2 }}
                    onClick={() => navigate('/inventory/purchases')}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4">
                    <EditIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Editar Orden de Compra
                </Typography>
            </Box>

            {!isEditable && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    Esta orden de compra no se puede editar porque su estado es {originalPurchase?.status}
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    {success}
                </Alert>
            )}

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Información General
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            select
                            fullWidth
                            required
                            label="Proveedor"
                            name="supplier_id"
                            value={purchaseData.supplier_id}
                            onChange={handleInputChange}
                            disabled={submitting || !isEditable}
                        >
                            <MenuItem value="">Seleccione un proveedor</MenuItem>
                            {suppliers.map((supplier) => (
                                <MenuItem key={supplier.id} value={supplier.id}>
                                    {supplier.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Fecha de Compra"
                            name="purchase_date"
                            value={purchaseData.purchase_date}
                            onChange={handleInputChange}
                            InputLabelProps={{ shrink: true }}
                            disabled={submitting || !isEditable}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            label="Número de Referencia"
                            name="reference_number"
                            value={purchaseData.reference_number}
                            onChange={handleInputChange}
                            placeholder="Ej: Factura #12345"
                            disabled={submitting || !isEditable}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <TextField
                                select
                                fullWidth
                                label="Estado"
                                name="status"
                                value={purchaseData.status}
                                onChange={handleInputChange}
                                disabled={submitting || !isEditable}
                                sx={{ mr: 1 }}
                            >
                                <MenuItem value="pending">Pendiente</MenuItem>
                                <MenuItem value="partial">Parcial</MenuItem>
                                <MenuItem value="received">Recibido</MenuItem>
                                <MenuItem value="cancelled">Cancelado</MenuItem>
                            </TextField>
                            {getStatusChip(purchaseData.status)}
                        </Box>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <TextField
                                select
                                fullWidth
                                label="Estado de Pago"
                                name="payment_status"
                                value={purchaseData.payment_status}
                                onChange={handleInputChange}
                                disabled={submitting || !isEditable}
                                sx={{ mr: 1 }}
                            >
                                <MenuItem value="pending">Pendiente</MenuItem>
                                <MenuItem value="partial">Parcial</MenuItem>
                                <MenuItem value="paid">Pagado</MenuItem>
                                <MenuItem value="cancelled">Cancelado</MenuItem>
                            </TextField>
                            {getPaymentStatusChip(purchaseData.payment_status)}
                        </Box>
                    </Grid>

                    {purchaseData.payment_status === 'paid' && (
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                select
                                fullWidth
                                required={purchaseData.payment_status === 'paid'}
                                label="Cuenta de Pago"
                                name="account_id"
                                value={purchaseData.account_id || ''}
                                onChange={handleInputChange}
                                disabled={submitting || !isEditable}
                            >
                                <MenuItem value="">Seleccione cuenta de egreso</MenuItem>
                                {accounts.map((account) => (
                                    <MenuItem key={account.id} value={account.id}>
                                        {account.name} (Saldo: S/ {parseFloat(account.current_balance || 0).toFixed(2)})
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Notas"
                            name="notes"
                            value={purchaseData.notes}
                            onChange={handleInputChange}
                            placeholder="Detalles adicionales sobre esta orden de compra"
                            disabled={submitting || !isEditable}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {isEditable && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Agregar Productos
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                select
                                fullWidth
                                required
                                label="Producto"
                                name="item_id"
                                value={currentItem.item_id}
                                onChange={handleItemInputChange}
                                disabled={submitting}
                            >
                                <MenuItem value="">Seleccione un producto</MenuItem>
                                {products.map((product) => (
                                    <MenuItem key={product.id} value={product.id}>
                                        {product.name} ({product.sku || 'Sin SKU'})
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                required
                                type="number"
                                label="Cantidad"
                                name="quantity"
                                value={currentItem.quantity}
                                onChange={handleItemInputChange}
                                InputProps={{ inputProps: { min: 1 } }}
                                disabled={submitting}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                required
                                type="number"
                                label="Precio Unitario"
                                name="unit_price"
                                value={currentItem.unit_price}
                                onChange={handleItemInputChange}
                                InputProps={{
                                    inputProps: { min: 0, step: 0.01 },
                                    startAdornment: '$'
                                }}
                                disabled={submitting}
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Notas del Producto"
                                name="notes"
                                value={currentItem.notes}
                                onChange={handleItemInputChange}
                                disabled={submitting}
                            />
                        </Grid>

                        <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={addItemToPurchase}
                                disabled={submitting || !currentItem.item_id}
                                sx={{ height: '56px' }}
                            >
                                Agregar
                            </Button>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />
                </Paper>
            )}

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Productos en la Orden ({purchaseData.items.length})
                </Typography>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Producto</TableCell>
                                <TableCell align="center">Cantidad</TableCell>
                                <TableCell align="right">Precio Unitario</TableCell>
                                <TableCell align="right">Subtotal</TableCell>
                                {isEditable && <TableCell align="center">Acción</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {purchaseData.items.length > 0 ? (
                                purchaseData.items.map((item, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                {item.item_name}
                                            </Typography>
                                            {item.notes && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Nota: {item.notes}
                                                </Typography>
                                            )}

                                            {/* Si es serializado y la orden se marca como recibida, mostrar botón de configurar series */}
                                            {(() => {
                                                const product = products.find(p => p.id === item.item_id);
                                                const isSerialized = product && !!product.equipment_type;
                                                if (isSerialized && purchaseData.status === 'received' && isEditable) {
                                                    return (
                                                        <Box sx={{ mt: 1, mb: 1 }}>
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                color={item.units && item.units.length === item.quantity && item.units.every(u => u.serial_number && u.mac_address) ? "success" : "warning"}
                                                                startIcon={<ScanIcon />}
                                                                onClick={() => handleOpenSeriesDialog(index)}
                                                            >
                                                                {item.units && item.units.length === item.quantity && item.units.every(u => u.serial_number && u.mac_address)
                                                                    ? `Series Listas (${item.quantity})`
                                                                    : `Configurar Series (${item.quantity})`}
                                                            </Button>
                                                        </Box>
                                                    );
                                                }
                                                return null;
                                            })()}

                                            {item.units && item.units.length > 0 && (
                                                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {item.units.map((u, uidx) => (
                                                        <Tooltip title={`MAC: ${u.mac_address}`} key={uidx}>
                                                            <Chip 
                                                                label={`S/N: ${u.serial_number}`} 
                                                                size="small" 
                                                                variant="outlined" 
                                                                color="primary"
                                                                sx={{ fontFamily: 'monospace', fontSize: '0.75rem', height: '20px' }}
                                                            />
                                                        </Tooltip>
                                                    ))}
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">{item.quantity}</TableCell>
                                        <TableCell align="right">${parseFloat(item.unit_price).toFixed(2)}</TableCell>
                                        <TableCell align="right">${(item.quantity * item.unit_price).toFixed(2)}</TableCell>
                                        {isEditable && (
                                            <TableCell align="center">
                                                <IconButton
                                                    color="error"
                                                    size="small"
                                                    onClick={() => removeItem(index)}
                                                    disabled={submitting}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={isEditable ? 5 : 4} align="center">
                                        No hay productos agregados a la orden
                                    </TableCell>
                                </TableRow>
                            )}
                            {purchaseData.items.length > 0 && (
                                <TableRow>
                                    <TableCell colSpan={2} align="right">
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            Total:
                                        </Typography>
                                    </TableCell>
                                    <TableCell />
                                    <TableCell align="right">
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            ${totalAmount.toFixed(2)}
                                        </Typography>
                                    </TableCell>
                                    {isEditable && <TableCell />}
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                    variant="outlined"
                    onClick={() => navigate('/inventory/purchases')}
                    disabled={submitting}
                >
                    Cancelar
                </Button>
                {isEditable && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={handleSubmit}
                        disabled={submitting || purchaseData.items.length === 0}
                    >
                        {submitting ? 'Guardando...' : 'Actualizar Orden de Compra'}
                    </Button>
                )}
            </Box>

            {/* DIALOG DE CAPTURA DE SERIES (NÚMEROS DE SERIE / MACS) */}
            <Dialog open={seriesDialogOpen} onClose={() => setSeriesDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'white' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ScanIcon />
                        Configurar Números de Serie y MAC
                    </Typography>
                    <IconButton onClick={() => setSeriesDialogOpen(false)} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 3 }}>
                    {seriesError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {seriesError}
                        </Alert>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Ingrese o escanee el número de serie y la dirección MAC para cada una de las <strong>{purchaseData.items[activeItemIndex]?.quantity}</strong> unidades del equipo.
                    </Typography>

                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell align="center" sx={{ width: '80px', fontWeight: 'bold' }}>#</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Número de Serie (S/N)</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Dirección MAC</TableCell>
                                    <TableCell sx={{ width: '150px', fontWeight: 'bold' }}>Estado / Condición</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tempSeries.map((unit, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>{idx + 1}</TableCell>
                                        <TableCell>
                                            <TextField
                                                fullWidth
                                                required
                                                size="small"
                                                placeholder="Ej: SN123456789"
                                                value={unit.serial_number}
                                                onChange={(e) => handleTempSeriesChange(idx, 'serial_number', e.target.value)}
                                                inputProps={{ style: { textTransform: 'uppercase', fontFamily: 'monospace' } }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                fullWidth
                                                required
                                                size="small"
                                                placeholder="Ej: AA:BB:CC:DD:EE:FF"
                                                value={unit.mac_address}
                                                onChange={(e) => handleTempSeriesChange(idx, 'mac_address', e.target.value)}
                                                inputProps={{ style: { textTransform: 'uppercase', fontFamily: 'monospace' } }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                select
                                                fullWidth
                                                size="small"
                                                value={unit.condition || 'new'}
                                                onChange={(e) => handleTempSeriesChange(idx, 'condition', e.target.value)}
                                            >
                                                <MenuItem value="new">Nuevo</MenuItem>
                                                <MenuItem value="used">Usado</MenuItem>
                                                <MenuItem value="reconditioned">Reacondicionado</MenuItem>
                                            </TextField>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setSeriesDialogOpen(false)} variant="outlined">
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSaveSeries}
                        variant="contained"
                        color="success"
                        startIcon={<SaveIcon />}
                        disabled={tempSeries.length === 0}
                    >
                        Guardar Series
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EditPurchase;